import { useState, useRef, useEffect } from 'react'
import Overview from './Overview'
import Assignments from './Assignments'
import Settings from './Settings'
import Pathways from './Pathways'
import MyPathways from './MyPathways'
import { getUserInitials, resolveAvatarSrc } from 'utils/avatar'
import { useContext } from 'react'
import { ProfileContext } from 'pages/Profile'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { isPathwaysEligible } from 'utils/acl'
import RightSidebar from 'components/navigation/RightSidebar'

export default function Menu() {
  const { profile, users = [] } = useContext(ProfileContext)
  const { user: authUser } = useSelector(x => x.auth)
  const location = useLocation()
  const navigate = useNavigate()
  const isOwnProfile = authUser?.user?._id === profile?._id

  const [activeTab, setActiveTab] = useState('overview')
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const toggleRef = useRef(null)
  const panelRef = useRef(null)

  const filteredUsers = users && searchTerm.trim()
    ? users
      .filter((u) => {
        const firstName = String(u?.firstName || '').toLowerCase()
        const lastName = String(u?.lastName || '').toLowerCase()
        const email = String(u?.email || '').toLowerCase()
        const search = searchTerm.toLowerCase()
        return firstName.includes(search) || lastName.includes(search) || email.includes(search)
      })
      .slice(0, 5)
    : []

  useEffect(() => {
    if (!menuOpen) return
    function onPointerDown(e) {
      if (panelRef.current && panelRef.current.contains(e.target)) return
      if (toggleRef.current && toggleRef.current.contains(e.target)) return
      setMenuOpen(false)
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  const tabs = [
    { key: 'overview', label: 'Overview', icon: 'bi bi-person', show: true },
    { key: 'assignments', label: 'Assignments', icon: 'bi bi-calendar-check', show: true },
    { key: 'pathways', label: 'Pathways', icon: 'bi bi-signpost-split', show: isPathwaysEligible(profile.acl) },
    { key: 'mypathways', label: isOwnProfile ? 'My Pathways' : `${profile.firstName || 'Member'}'s Pathways`, icon: 'bi bi-map', show: isOwnProfile || isPathwaysEligible(profile.acl) },
    { key: 'settings', label: 'Settings', icon: 'bi bi-gear', show: true },
  ].filter(t => t.show)

  useEffect(() => {
    const validTabKeys = tabs.map((tab) => tab.key)
    const tabFromState = String(location?.state?.profileTab || '').trim().toLowerCase()

    if (tabFromState && validTabKeys.includes(tabFromState)) {
      if (activeTab !== tabFromState) {
        setActiveTab(tabFromState)
      }
      // Consume one-shot navigation state so tab selection is not forced on subsequent renders.
      const nextState = { ...(location?.state || {}) }
      delete nextState.profileTab
      delete nextState.source
      delete nextState.at
      navigate(location.pathname, {
        replace: true,
        state: Object.keys(nextState).length > 0 ? nextState : null,
      })
      return
    }

    if (!validTabKeys.includes(activeTab)) {
      setActiveTab(validTabKeys[0] || 'overview')
    }
  }, [activeTab, location.pathname, location?.state, navigate, tabs])

  const handleSelect = (key) => {
    setActiveTab(key)
    setMenuOpen(false)
  }

  const handleOpenProfile = (targetUserId) => {
    if (!targetUserId) return
    navigate(`/profile/${targetUserId}`)
    setMenuOpen(false)
    setSearchTerm('')
  }

  const activeLabel = tabs.find(t => t.key === activeTab)?.label || ''
  const activeIcon = tabs.find(t => t.key === activeTab)?.icon || 'bi bi-person'

  return (
    <>
      {/* Toggle button */}
      <div ref={toggleRef} className="right-menu">
        <button
          className="right-menu-toggle"
          onClick={() => setMenuOpen(v => !v)}
          aria-label="Toggle profile menu"
          title={activeLabel}
        >
          <i className={activeIcon}></i>
        </button>
      </div>

      {/* Slide-in panel */}
      <RightSidebar label="Profile Options" icon="bi-person-gear" open={menuOpen} onClose={() => setMenuOpen(false)} panelRef={panelRef}>
            <div className="right-menu-header">
              <span className="right-menu-title">Profile</span>
              <button className="right-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close">
                &times;
              </button>
            </div>
            <div className="right-menu-items">
              <div className="right-menu-search">
                <i className="bi bi-search right-menu-search-icon"></i>
                <input
                  type="text"
                  className="right-menu-search-input"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoComplete="off"
                />
                {searchTerm && (
                  <button
                    className="right-menu-search-clear"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
              {filteredUsers.map((user) => (
                (() => {
                  const avatarSrc = resolveAvatarSrc(user?.avatar)
                  const userInitials = getUserInitials(user, 'U')
                  return (
                    <button
                      key={user._id}
                      className="right-menu-item"
                      onClick={() => handleOpenProfile(user._id)}
                    >
                      {avatarSrc ? (
                        <img
                          src={avatarSrc}
                          alt={`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User avatar'}
                          style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                        />
                      ) : (
                        <span
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            background: 'rgba(255,255,255,0.12)',
                            flexShrink: 0,
                          }}
                        >
                          {userInitials}
                        </span>
                      )}
                      <span>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}</span>
                    </button>
                  )
                })()
              ))}
              {searchTerm && filteredUsers.length === 0 && (
                <div className="right-menu-item" style={{ opacity: 0.7, cursor: 'default' }}>
                  <i className="bi bi-search"></i>
                  <span>No users found</span>
                </div>
              )}
              <hr className="right-menu-divider" />
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  className={`right-menu-item${activeTab === tab.key ? ' active' : ''}`}
                  onClick={() => handleSelect(tab.key)}
                >
                  <i className={tab.icon}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
      </RightSidebar>

      {/* Active tab content */}
      <div className="mt-3">
        {activeTab === 'overview' && <Overview />}
        {activeTab === 'assignments' && <Assignments />}
        {activeTab === 'pathways' && isPathwaysEligible(profile.acl) && <Pathways tabActive />}
        {activeTab === 'mypathways' && <MyPathways tabActive />}
        {activeTab === 'settings' && <Settings />}
      </div>
    </>
  )
}
